import React, { useEffect, useState } from 'react';
import {
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  message,
  Upload,
  GetProp,
  UploadProps,
  Image,
  Button,
  Input,
  Switch,
} from 'antd';
import { useGlobalContext } from '@src/context/globalContext';
import {
  uploadImageFile,
} from '@src/shared/utils/VideoGeneration/uploadAsset';
import './uploadImage.less';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const beforeUpload = (file: FileType) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message.error('You can only upload JPG/PNG file!');
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error('Image must smaller than 2MB!');
  }
  return isJpgOrPng && isLt2M;
};

const UploadImage: React.FC = () => {
  const {
    ScriptDetails: [scriptDetails, setScriptDetails],
  } = useGlobalContext();
  const [textOver, setTextOver] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isTextVisible, setIsTextVisible] = useState(
    scriptDetails?.thumbnailTextVisible || false
  );
  const [loading, setLoading] = useState(false);

  const handleChange: UploadProps['onChange'] = async (info) => {
    setUploadedFile(info.file.originFileObj || null);
  };

  const uploadButton = (
    <Button icon={<UploadOutlined />}>
      Upload File
    </Button>
  );

  useEffect(() => {
    setPreviewImage(scriptDetails?.thumbnailImage || '');
  }, []);

  const uploadImage = async () => {
    if (uploadedFile) {
      try {
        setLoading(true);
        const response = await uploadImageFile(uploadedFile, '', '');
        setPreviewImage(response.image_url);
        setScriptDetails((prevScript: { data: any }) => {
          const updatedScript = { ...prevScript };
          return {
            ...updatedScript,
            thumbnailImage: response.image_url,
            thumbnailText: textOver,
            thumbnailTextVisible: isTextVisible,
          };
        });
        message.success('Changes saved');
      } catch {
        message.warning('Failed to upload set thumbnail image');
      } finally {
        setLoading(false);
      }
    } else {
      setScriptDetails((prevScript: { data: any }) => {
        const updatedScript = { ...prevScript };
        return {
          ...updatedScript,
          thumbnailText: textOver,
          thumbnailTextVisible: isTextVisible,
        };
      });
      message.success('Changes saved');
    }
  };

  return (
    <div className="upload-image">
      <div className="header-section">
        <h3>Thumbnail</h3>
        <Switch
          onChange={(e) => {
            setScriptDetails((prevScript: { data: any }) => {
              const updatedScript = { ...prevScript };
              return {
                ...updatedScript,
                showThumbnail: e
              };
            });
          }}
          defaultChecked={scriptDetails?.showThumbnail}
          value={scriptDetails?.showThumbnail}
        />
      </div>

      <div className="preview-section">
        <Image
          src={previewImage}
          alt="Thumbnail preview"
        />
      </div>

      <div className="upload-section">
        <h3>File</h3>
        <Upload
          showUploadList={false}
          onChange={handleChange}
          accept="image/*"
          beforeUpload={beforeUpload}
        >
          {uploadButton}
        </Upload>
        {uploadedFile && <p className="file-name">{uploadedFile.name}</p>}
      </div>

      <div className="text-over-section">
        <div className="section-header">
          <h3>Enter Text Over</h3>
          <Switch
            onChange={(e) => setIsTextVisible(e)}
            defaultChecked={scriptDetails?.thumbnailTextVisible || false}
            value={isTextVisible}
          />
        </div>
        <Input
          placeholder="Enter Text over"
          defaultValue={textOver}
          onChange={(e) => setTextOver(e.target.value)}
        />
      </div>

      <Button
        className="save-button"
        onClick={uploadImage}
        loading={loading}
      >
        Save Changes
      </Button>
    </div>
  );
};

export default UploadImage;
